import '@/styles/reset.scss';
import '@/styles/main.scss';
import '@/styles/global.scss';
import '@/styles/content.scss';

import i18next from 'i18next';
import type { AppProps } from 'next/app';
import { appWithTranslation } from 'next-i18next';
import React, { useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';
import { SWRConfig } from 'swr';

import { fetcher } from '@/api';
import initFontawesome from '@/hooks/init-fontawesome';
import { logPerformanceMetrics } from '@/utils/performance';

import public_en from '../../public/locales/en/public.json';

// Initialize i18next synchronously before component render
if (!i18next.isInitialized) {
  i18next.init({
    interpolation: { escapeValue: false },
    lng: 'en',
    resources: {
      en: {
        public: public_en
      }
    }
  });
}

const PublicApp = ({ Component, pageProps }: AppProps) => {
  useEffect(() => {
    try {
      // Initialize FontAwesome after initial render (deferred for performance)
      const timeoutId = setTimeout(() => {
        try {
          initFontawesome();
        } catch (error) {
          console.error('Error initializing FontAwesome:', error);
        }
      }, 0);

      // Log performance metrics in development
      if (process.env.NODE_ENV === 'development') {
        logPerformanceMetrics();
      }

      return () => clearTimeout(timeoutId);
    } catch (error) {
      console.error('Error in initialization useEffect:', error);
    }
  }, []);

  return (
    <React.StrictMode>
      <SWRConfig
        value={{
          fetcher,
          revalidateOnFocus: false,
          revalidateOnReconnect: true,
          revalidateIfStale: false,
          dedupingInterval: 10000,
          focusThrottleInterval: 10000,
          loadingTimeout: 5000,
          errorRetryCount: 3,
          errorRetryInterval: 5000,
          shouldRetryOnError: true,
          keepPreviousData: true
        }}
      >
        <I18nextProvider i18n={i18next}>
          <Component {...pageProps} />
        </I18nextProvider>
      </SWRConfig>
    </React.StrictMode>
  );
};

export default appWithTranslation(PublicApp);
